import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  connectorsAPI,
  ConnectorRegistryEntry,
  ConnectorConnection,
} from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
// Modal order (connectorGroups): HeyGen first, then each group, server order within one.
import { categoryRank } from './connectorGroups';

/**
 * ConnectorsModalProvider — single source of truth for connector UX.
 *
 * Replaces the standalone /connectors page (now a one-line shim that
 * navigates here). Every entry point — task composer, dashboard composer,
 * sidebar nav, marketing deep-link — flows through the same URL-param
 * contract this provider derives.
 *
 *   ┌─ URL → state ─────────────────────────────────────────────────┐
 *   │ ?connectors                              → 'overview'          │
 *   │ ?connectors&connector=heygen             → 'manage'            │
 *   │ ?connector=unknown                       → 'overview' fallback │
 *   │ (none)                                   → 'closed'            │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Bring-your-own return (server buildReturnUrl, after /connectors/callback):
 *   ?connectors=&connector=<id>&result=connected|cancelled|error[&reason=<code>]
 * opens that connector's panel with one result line (connectResult below);
 * `result`/`reason` then leave the URL and the connections are refetched,
 * because the provider fetched them before the callback finished.
 *
 * Bring-your-own entries (connectMode 'connect') only show for accounts with
 * connectorsEnabled; the registry is ordered by category (HeyGen first).
 *
 * Legacy OAuth toast params (status, oauth_connector, reason) are still
 * recognised by the URL-param scrubber so any stale bookmark / cached
 * redirect doesn't strand garbage in the URL bar. They no longer drive
 * UI — Wave 3 of the HeyGen migration removed OAuth.
 *
 * The provider also owns the registry+connections fetch — every consumer
 * (picker popover, overview modal, manage panel) reads from the same
 * context so the picker never disagrees with the modal about what's
 * enabled.
 *
 * Param hygiene: close() removes ONLY the connector-owned keys
 * (CONNECTOR_PARAMS) and preserves anything else on the URL — future
 * `?tab=files` / `?artifact=…` flows are unaffected. Every URL transition
 * uses { replace: true } so the browser back stack doesn't fill with
 * "click back 12 times to actually go back" history entries.
 */

type ModalView = 'closed' | 'overview' | 'manage';

const CONNECTOR_PARAMS = [
  'connectors',
  'connector',
  'status',
  'oauth_connector',
  'reason',
  'result',
] as const;


/** What the modal shows after coming back from a service's consent page. */
export interface ConnectResult {
  connectorId: string;
  result: string; // 'connected' | 'cancelled' | 'error' | unknown
  reason: string | null;
  accountLabel: string | null;
  accountSwitched: boolean;
}

interface OpenState {
  view: ModalView;
  connectorId: string | null;
  oauthStatus: 'connected' | 'error' | null;
  oauthConnectorId: string | null;
  oauthReason: string | null;
}

interface ConnectorsModalContextValue {
  // URL-derived state.
  view: ModalView;
  connectorId: string | null;
  // Server data (shared between picker + overview + manage).
  registry: ConnectorRegistryEntry[];
  connections: ConnectorConnection[];
  connectionByConnectorId: Record<string, ConnectorConnection>;
  loading: boolean;
  error: string | null;
  // The one-line outcome of a bring-your-own connect, for its panel.
  connectResult: ConnectResult | null;
  // Imperative API. triggerRef preserves focus return on close.
  open: (triggerRef?: HTMLElement | null) => void;
  openForConnector: (id: string, triggerRef?: HTMLElement | null) => void;
  close: () => void;
  refresh: () => Promise<void>;
}

const ConnectorsModalContext = createContext<ConnectorsModalContextValue | null>(null);

export function useConnectorsModal(): ConnectorsModalContextValue {
  const ctx = useContext(ConnectorsModalContext);
  if (!ctx) {
    throw new Error('useConnectorsModal must be used inside ConnectorsModalProvider');
  }
  return ctx;
}

function deriveOpenState(searchParams: URLSearchParams): OpenState {
  const hasConnectors = searchParams.has('connectors');
  const connector = searchParams.get('connector');
  const oauthStatus = searchParams.get('status');
  const oauthConnector = searchParams.get('oauth_connector');
  const oauthReason = searchParams.get('reason');

  // OAuth returns from the server land with `?status=connected&oauth_connector=heygen`
  // (and no `connectors` flag set by the server). We still want the modal
  // to open on return — treat any oauth status param as implicitly setting
  // the overview view so the user sees the toast in context.
  const implicitOpen = !!oauthStatus;

  if (!hasConnectors && !implicitOpen) {
    return {
      view: 'closed',
      connectorId: null,
      oauthStatus: null,
      oauthConnectorId: null,
      oauthReason: null,
    };
  }

  return {
    view: connector ? 'manage' : 'overview',
    connectorId: connector || null,
    oauthStatus: oauthStatus === 'connected' || oauthStatus === 'error'
      ? (oauthStatus as 'connected' | 'error')
      : null,
    oauthConnectorId: oauthConnector,
    oauthReason: oauthReason,
  };
}

function clearConnectorParams(prev: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const key of CONNECTOR_PARAMS) next.delete(key);
  return next;
}

interface ProviderProps {
  children: React.ReactNode;
}

export const ConnectorsModalProvider: React.FC<ProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { success, error: notifyError } = useNotification();
  const { user } = useAuth();
  const byoVisible = !!(user && (user as { connectorsEnabled?: boolean }).connectorsEnabled);

  // Server data — shared across surfaces.
  const [rawRegistry, setRegistry] = useState<ConnectorRegistryEntry[]>([]);
  const [connectResult, setConnectResult] = useState<ConnectResult | null>(null);
  const consumedResultRef = useRef<string | null>(null);

  const registry = useMemo(() => {
    const shown = byoVisible ? rawRegistry : rawRegistry.filter((e) => e.connectMode !== 'connect');
    return shown
      .map((entry, i) => ({ entry, i }))
      .sort((a, b) => categoryRank(a.entry.category) - categoryRank(b.entry.category) || a.i - b.i)
      .map(({ entry }) => entry);
  }, [rawRegistry, byoVisible]);
  const [connections, setConnections] = useState<ConnectorConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track the element that opened the modal so we restore focus on close.
  // Stored in a ref so re-renders don't blow it away mid-flight.
  const triggerRef = useRef<HTMLElement | null>(null);

  // OAuth toast de-dupe: the consumed-set is keyed on the param tuple so
  // double-renders of the same redirect don't fire the toast twice.
  const consumedOauthRef = useRef<string | null>(null);

  // Memoize the URL derivation so 10+ consumers don't each re-derive on
  // every render. Cheap today, free at scale.
  const openState = useMemo(() => deriveOpenState(searchParams), [searchParams]);

  // Overlapping fetches (mount + a connect return) apply in call order: an
  // older answer arriving late never overwrites a newer one.
  const fetchSeqRef = useRef(0);
  const fetchAll = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setError(null);
    try {
      const [reg, conn] = await Promise.all([
        connectorsAPI.registry(),
        connectorsAPI.connections(),
      ]);
      if (seq !== fetchSeqRef.current) return;
      setRegistry(reg.data.connectors || []);
      setConnections(conn.data.connections || []);
    } catch (err: any) {
      if (seq !== fetchSeqRef.current) return;
      const msg = `Could not load connectors${err?.message ? `: ${err.message}` : ''}. Try again.`;
      setError(msg);
      notifyError(msg);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [notifyError]);

  // Initial fetch — only when authenticated. Mounting this provider above
  // the route tree means it would otherwise fetch on /auth too, hitting
  // 401 and triggering the api.ts redirect interceptor in a loop.
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    void fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // OAuth return handler. Fires once per redirect tuple, then clears the
  // toast params from the URL so a refresh doesn't replay.
  useEffect(() => {
    const { oauthStatus, oauthConnectorId, oauthReason } = openState;
    if (!oauthStatus) return;
    // Wait for the registry so the toast names the connector ("HeyGen is
    // ready"), not its raw id ("heygen is ready"). The first render after
    // an OAuth return always has an empty registry; firing then consumed
    // the dedupe key and locked in the id. If the fetch fails, loading still
    // flips false and we fall back to the id.
    if (loading) return;

    const dedupeKey = `${oauthStatus}|${oauthConnectorId || ''}|${oauthReason || ''}`;
    if (consumedOauthRef.current === dedupeKey) return;
    consumedOauthRef.current = dedupeKey;

    const entry = registry.find((e) => e.id === oauthConnectorId);
    const label = entry?.displayName || oauthConnectorId || 'App';

    if (oauthStatus === 'connected') {
      success(`${label} is ready`);
      // Pull fresh connections so the overview reflects the new connected
      // state without the user needing to refresh.
      void fetchAll();
    } else {
      const map: Record<string, string> = {
        state_mismatch: 'Connection attempt looked tampered. Try again.',
        oauth_denied: `${label} declined the connection. Try again.`,
        exchange_failed: `Couldn't finish ${label} connection. Try again.`,
        unknown_connector: 'Unknown app. Try again.',
      };
      notifyError(map[oauthReason || ''] || `Couldn't connect ${label}. Try again.`);
    }

    // Clear toast params (but keep `connectors` set so the modal stays open
    // for the user — feedback was: stay on overview after OAuth success).
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('status');
      next.delete('oauth_connector');
      next.delete('reason');
      // Make sure the modal stays open even though the implicit-open
      // trigger (status param) just went away.
      if (!next.has('connectors')) next.set('connectors', '');
      return next;
    }, { replace: true });
  }, [openState, registry, loading, success, notifyError, setSearchParams, fetchAll]);

  // Bring-your-own return: ?connector=<id>&result=…[&reason=…]. Keep the
  // outcome in state (the callback page passed the account label in
  // navigation state), refetch the now-stale connections, and drop the
  // result params while keeping the panel open on that connector.
  const resultParam = searchParams.get('result');
  const resultConnector = searchParams.get('connector');
  const resultReason = searchParams.get('reason');
  useEffect(() => {
    if (!resultParam || !resultConnector) return;
    // Legacy toast returns use `reason` with `status`; leave those alone.
    if (searchParams.get('status')) return;
    const key = `${resultConnector}|${resultParam}|${resultReason || ''}|${location.key}`;
    if (consumedResultRef.current === key) return;
    consumedResultRef.current = key;

    const navState = (location.state as { connectorResult?: Partial<ConnectResult> } | null)?.connectorResult;
    const fromNav = navState && navState.connectorId === resultConnector ? navState : null;
    setConnectResult({
      connectorId: resultConnector,
      result: resultParam,
      reason: resultReason,
      accountLabel: (fromNav && fromNav.accountLabel) || null,
      accountSwitched: !!(fromNav && fromNav.accountSwitched),
    });
    if (user) void fetchAll();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('result');
      next.delete('reason');
      if (!next.has('connectors')) next.set('connectors', '');
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultParam, resultConnector, resultReason]);

  // The result line belongs to that connector's panel only.
  useEffect(() => {
    if (!connectResult) return;
    if (openState.view !== 'manage' || openState.connectorId !== connectResult.connectorId) {
      setConnectResult(null);
    }
  }, [openState.view, openState.connectorId, connectResult]);

  // Restore focus to the trigger when the modal transitions to closed.
  // We watch view rather than searchParams so chained transitions (open
  // → manage → close) restore to the original opener, not the breadcrumb-
  // back intermediate state.
  const prevViewRef = useRef<ModalView>(openState.view);
  useEffect(() => {
    if (prevViewRef.current !== 'closed' && openState.view === 'closed') {
      const t = triggerRef.current;
      if (t && t.isConnected && typeof t.focus === 'function') {
        try { t.focus(); } catch { /* swallow */ }
      }
      triggerRef.current = null;
    }
    prevViewRef.current = openState.view;
  }, [openState.view]);

  const open = useCallback(
    (trigger?: HTMLElement | null) => {
      if (trigger) triggerRef.current = trigger;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('connectors', '');
        next.delete('connector');
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const openForConnector = useCallback(
    (id: string, trigger?: HTMLElement | null) => {
      if (trigger) triggerRef.current = trigger;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('connectors', '');
        next.set('connector', id);
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const close = useCallback(() => {
    setSearchParams(clearConnectorParams, { replace: true });
  }, [setSearchParams]);

  const refresh = useCallback(async () => {
    await fetchAll();
  }, [fetchAll]);

  const connectionByConnectorId = useMemo(() => {
    const map: Record<string, ConnectorConnection> = {};
    for (const c of connections) map[c.connectorId] = c;
    return map;
  }, [connections]);

  const value = useMemo<ConnectorsModalContextValue>(
    () => ({
      view: openState.view,
      connectorId: openState.connectorId,
      registry,
      connections,
      connectionByConnectorId,
      loading,
      error,
      connectResult,
      open,
      openForConnector,
      close,
      refresh,
    }),
    [
      openState.view,
      openState.connectorId,
      registry,
      connections,
      connectionByConnectorId,
      loading,
      error,
      connectResult,
      open,
      openForConnector,
      close,
      refresh,
    ]
  );

  // We DO NOT render the modals here — that's the App's job. The modals
  // import this context themselves. Keeping render-tree separate from
  // state-tree avoids the trap where adding/removing a provider also
  // adds/removes UI.
  return (
    <ConnectorsModalContext.Provider value={value}>
      {children}
    </ConnectorsModalContext.Provider>
  );
};

export default ConnectorsModalProvider;
